import { TFunction } from 'i18next';

import {
  AggregationMethod,
  aggregationMethodOptions,
  dataFieldOptions,
  LimitMode,
  limitModeOptions,
  LimitType,
  limitTypeOptions,
  percentExpressionField,
  POSITION_KPI,
  ValueType,
  valueTypeOptions,
} from './createInvestmentLimitModel';

export type DataFieldValue =
  (typeof dataFieldOptions)[number] | typeof POSITION_KPI;

const dataFieldValues = new Set<string>([POSITION_KPI, ...dataFieldOptions]);

export function isDataFieldValue(value: string): value is DataFieldValue {
  return dataFieldValues.has(value);
}

export function dataFieldLabel(t: TFunction, value: DataFieldValue) {
  return t(`app.investmentLimit.dataField.${value}`);
}

export function dataFieldSelectData(
  t: TFunction,
  values: readonly DataFieldValue[] = dataFieldOptions
) {
  return values.map((value) => ({
    label: dataFieldLabel(t, value),
    value,
  }));
}

export function limitTypeSelectData(t: TFunction) {
  return limitTypeOptions.map((option) => ({
    label: t(`app.investmentLimit.limitType.${option.id}`),
    value: option.value,
  }));
}

export function aggregationMethodSelectData(t: TFunction) {
  return aggregationMethodOptions.map((option) => ({
    label: t(`app.investmentLimit.aggregationMethod.${option.id}`),
    value: option.value,
  }));
}

export function aggregationMethodLabel(t: TFunction, value: AggregationMethod) {
  const option = aggregationMethodOptions.find((item) => item.value === value);
  if (option == null) {
    return value;
  }

  return t(`app.investmentLimit.aggregationMethod.${option.id}`);
}

export function limitModeCards(t: TFunction) {
  return limitModeOptions.map((option) => ({
    description: t(`app.investmentLimit.mode.${option.id}.description`),
    iconName: option.icon,
    label: t(`app.investmentLimit.mode.${option.id}.label`),
    value: option.value,
  }));
}

export function valueTypeCards(t: TFunction) {
  return valueTypeOptions.map((option) => ({
    description: t(`app.investmentLimit.valueType.${option.value}.description`),
    iconName: option.icon,
    label: t(`app.investmentLimit.valueType.${option.value}.label`),
    value: option.value,
  }));
}

export function limitModeLabel(t: TFunction, value: LimitMode) {
  const option = limitModeOptions.find((item) => item.value === value);
  if (option == null) {
    return value;
  }

  return t(`app.investmentLimit.mode.${option.id}.label`);
}

export function limitTypeLabel(t: TFunction, value: LimitType) {
  const option = limitTypeOptions.find((item) => item.value === value);
  if (option == null) {
    return value;
  }

  return t(`app.investmentLimit.limitType.${option.id}`);
}

export function valueTypeLabel(t: TFunction, value: ValueType) {
  return t(`app.investmentLimit.valueType.${value}.label`);
}

export function expressionValueSuffix(
  limitMode: LimitMode,
  valueType: ValueType,
  field: 'evalValue' | 'limitValue'
) {
  return percentExpressionField(limitMode, valueType) === field
    ? '%'
    : undefined;
}
